import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Input, Space, Typography } from "antd";
import type { Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps,
    type TableSortChangeContext
} from "zhihao-ui-react";

import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { BILL_STATE, SERVICE_COST_TYPE } from "@/meta/expenseManage/serviceCost";
import { dateFormat, dateFullTimeFormat } from "@/utils";
import { formatNumber } from "@/utils/number";
import { useUserStore } from "@/stores/react/authStore";
import { loadServiceCostBills, type ServiceCostBillQuery, type ServiceCostBillRow } from "../../ServiceCostBillPage";
import { revokeServiceCostBill, verifyServiceCostBill } from "../../ServiceCostBillDetailPage";
import type { ServiceCostBillVerificationValues } from "../../ServiceCostBillVerificationModal";
import VerificationDialog from "./verificationDialog";

export interface AccountBillQuery extends ServiceCostBillQuery {
    customerId: number;
}

export interface BillRecordProps {
    id: number | string;
    loadBills?: (query: AccountBillQuery) => Promise<Page<ServiceCostBillRow>>;
    verifyBill?: (params: {
        id: number | string;
        verificationAmount: number;
        verificationTime: string;
        verificationFileId?: number;
    }) => Promise<unknown>;
    revokeBill?: (id: number | string) => Promise<unknown>;
    onUpdated?: () => void;
}

const displayDate = (value?: string) => (value ? dateFormat(value) : DEFAULT_VALUE);

/** 账户详情的明细账单，保留旧 Vue 的日期筛选、排序、项目跳转和核销流程。 */
export default function BillRecord({
    id,
    loadBills = query => loadServiceCostBills(query),
    verifyBill = verifyServiceCostBill,
    revokeBill = revokeServiceCostBill,
    onUpdated
}: BillRecordProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<ServiceCostBillRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<AccountBillQuery>({ page: 1, size: 10, customerId: Number(id) });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");
    const [verificationBill, setVerificationBill] = useState<ServiceCostBillRow>();
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [revokeBillRow, setRevokeBillRow] = useState<ServiceCostBillRow>();
    const [revokeLoading, setRevokeLoading] = useState(false);

    useEffect(() => {
        const customerId = Number(id);
        // 首次挂载时查询已包含当前账户，避免 React 额外创建查询对象导致 Vue ProTable 的重复请求。
        setQuery(current => (current.customerId === customerId ? current : { ...current, customerId, page: 1 }));
    }, [id]);

    useEffect(() => {
        let disposed = false;
        void loadBills(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("明细账单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadBills, query]);

    const submitFilters = () => {
        if (startDate && endDate && startDate > endDate) {
            setError("创建时间范围无效");
            return;
        }
        setQuery(current => ({
            ...current,
            page: 1,
            startTime: startDate ? dateFormat(startDate, LOG_DATE_FORMAT_START) : undefined,
            endTime: endDate ? dateFullTimeFormat(endDate, LOG_DATE_FORMAT_END) : undefined
        }));
    };

    const resetFilters = () => {
        setStartDate("");
        setEndDate("");
        setError("");
        setQuery({ page: 1, size: 10, customerId: Number(id) });
    };

    const reload = async () => {
        const result = await loadBills(query);
        setRows(result.list || []);
        setTotal(result.total || 0);
    };

    const submitVerification = async (values: ServiceCostBillVerificationValues) => {
        if (!verificationBill) return;
        setVerificationLoading(true);
        try {
            await verifyBill({
                id: verificationBill.id,
                verificationAmount: values.amount,
                verificationTime: values.date,
                verificationFileId: values.fileId
            });
            await reload();
            setVerificationBill(undefined);
            setVerificationOpen(false);
            onUpdated?.();
        } catch {
            setError("账单核销失败");
        } finally {
            setVerificationLoading(false);
        }
    };

    const submitRevoke = async () => {
        if (!revokeBillRow) return;
        setRevokeLoading(true);
        try {
            await revokeBill(revokeBillRow.id);
            await reload();
            setRevokeBillRow(undefined);
            onUpdated?.();
        } catch {
            setError("账单撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    const handleSortChange = ({ prop, order }: TableSortChangeContext<ServiceCostBillRow>) => {
        if (prop !== "createdTime") return;
        setQuery(current => ({
            ...current,
            page: 1,
            sortKey: order ? "created_time" : undefined,
            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
        }));
    };

    const columns: TableColumnProps<ServiceCostBillRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 150,
            prop: "id",
            render: ({ row }) => (
                <Button type="link" onClick={() => navigate(`/expenseManagement/serviceCostBill/detail/${row.id}`)}>
                    {row.id}
                </Button>
            )
        },
        {
            key: "projectName",
            label: "关联项目",
            minWidth: 228,
            prop: "projectName",
            render: ({ row }) =>
                row.projectId ? (
                    <Button type="link" onClick={() => navigate(`/purchasingManage/project/info/${row.projectId}`)}>
                        {row.projectName || DEFAULT_VALUE}
                    </Button>
                ) : (
                    row.projectName || DEFAULT_VALUE
                )
        },
        {
            key: "amount",
            label: "账单金额（元）",
            minWidth: 214,
            prop: "amount",
            render: ({ row }) => formatNumber(row.amount, 2)
        },
        {
            key: "verificationAmount",
            label: "实际核销金额（元）",
            minWidth: 214,
            prop: "verificationAmount",
            render: ({ row }) =>
                row.verificationState === BILL_STATE.NOT_WRITTEN_OFF.code
                    ? DEFAULT_VALUE
                    : formatNumber(row.verificationAmount, 2)
        },
        {
            key: "type",
            label: "结算方式",
            minWidth: 214,
            prop: "type",
            render: ({ row }) => SERVICE_COST_TYPE.getIntroByCode(row.type) || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            minWidth: 130,
            prop: "createdTime",
            sortable: "custom",
            render: ({ row }) => displayDate(row.createdTime)
        },
        {
            key: "verificationState",
            label: "状态",
            width: 100,
            prop: "verificationState",
            render: ({ row }) => BILL_STATE.getIntroByCode(row.verificationState) || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 200,
            prop: "id",
            render: ({ row }) =>
                row.verificationState === BILL_STATE.NOT_WRITTEN_OFF.code ? (
                    <Button
                        disabled={banButton}
                        type="link"
                        onClick={() => {
                            setVerificationBill(row);
                            setVerificationOpen(true);
                        }}
                    >
                        核销
                    </Button>
                ) : (
                    <Button disabled={banButton} type="link" onClick={() => setRevokeBillRow(row)}>
                        撤销
                    </Button>
                )
        }
    ];

    return (
        <div aria-label="user-account-bill-record">
            <ZhTable<ServiceCostBillRow>
                data={rows}
                columns={normalizeOperationColumns(columns, rows)}
                pagination={false}
                rowKey="id"
                onSortChange={handleSortChange}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4 flex items-center justify-between">
                            <Typography.Title level={5} className="m-0">
                                明细账单
                            </Typography.Title>
                        </div>

                        <Space className="mb-4" wrap>
                            <Input
                                aria-label="创建时间起始"
                                placeholder="起始时间"
                                type="date"
                                value={startDate}
                                onChange={event => setStartDate(event.target.value)}
                            />
                            <Input
                                aria-label="创建时间结束"
                                placeholder="结束时间"
                                type="date"
                                value={endDate}
                                onChange={event => setEndDate(event.target.value)}
                            />
                            <Button onClick={submitFilters}>查询</Button>
                            <Button onClick={resetFilters}>重置</Button>
                        </Space>

                        {error ? (
                            <div className="mb-3 text-red-500" role="alert">
                                {error}
                            </div>
                        ) : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            hideOnSinglePage
                            onChange={page => setQuery(current => ({ ...current, page }))}
                        />
                    </div>
                }
            />
            <VerificationDialog
                open={verificationOpen}
                amount={verificationBill?.amount}
                loading={verificationLoading}
                disabled={banButton}
                onCancel={() => setVerificationOpen(false)}
                onVerification={values => void submitVerification(values)}
            />
            {revokeBillRow ? (
                <RevokeBillConfirm
                    loading={revokeLoading}
                    disabled={banButton}
                    onCancel={() => setRevokeBillRow(undefined)}
                    onConfirm={() => void submitRevoke()}
                />
            ) : null}
        </div>
    );
}

interface RevokeBillConfirmProps {
    loading: boolean;
    disabled: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/** 账单撤销确认，保留旧服务层 confirmable 的确认文案。 */
function RevokeBillConfirm({ loading, disabled, onCancel, onConfirm }: RevokeBillConfirmProps) {
    return (
        <ZhDialog
            title="撤销账单"
            open
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button disabled={disabled} loading={loading} type="primary" onClick={onConfirm}>
                        确定
                    </Button>
                </Space>
            }
        >
            是否撤销，撤销后账户余额将发生变化，请谨慎操作
        </ZhDialog>
    );
}
